/**
 * @file The host-facing half of the multi-part Signify fetch modal. Consumer: `ArTableControls`,
 * which spreads the return onto `<SignifyFetchJobModal/>` and hands `openModal` to the Signify menu,
 * exactly as it does for the configuration sidebar.
 * It owns whether the modal is open and, because it is mounted for as long as the table is on
 * screen, the lifetime of the job's two stores: connecting here rather than under the modal is what
 * lets a fetch keep streaming into the proxies while its modal is closed. The stores are refcounted,
 * so several tables on one form connecting the same job is harmless.
 * The reattach read runs only while this table has no job, and the active job is deliberately absent
 * from its dep array, so "Start a new list" is not immediately undone by the job it just let go of —
 * the dismissal is recorded in `dismissedSignifyFetchJobs`, which the query itself skips. It picks up
 * a fetch that finished while the tab was closed as well as a running one, which is why it is not
 * named for either. A rejected read is warned and left; the next `enabled` flip retries it.
 * `enabled` is the condition `ArTableControls` gates the modal itself on. Both effects wait for it,
 * because the hook is called on every ArTable and the reattach read is a `getDocs` — without the
 * gate every table in every org would pay for a query whose answer it could never show.
 * `configDisabledReason` comes from the org's mapping configuration and is the same reason that
 * disables the menu item, so the two can never disagree.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { UserContext } from '@/providers/UserContext';
import { uiState } from '@/store/uiState';
import { useSignifyMappings } from '../../../useSignifyMappings';
import { createSignifyFetchJobStores, findReattachableSignifyFetchJob } from './services';
import type { SignifyFetchJobModalProps } from '../index.types';

type UseSignifyFetchJobModalParams = Pick<
  SignifyFetchJobModalProps,
  'targetKey' | 'hasQuantityColumn' | 'disabled' | 'onSuccess' | 'onManualInsert' | 'onFindInsertedRows'
> & {
  /** Whether this table shows the modal at all; false means neither effect below may read. */
  enabled: boolean;
};

export const useSignifyFetchJobModal = ({
  targetKey,
  hasQuantityColumn,
  disabled,
  enabled,
  onSuccess,
  onManualInsert,
  onFindInsertedRows,
}: UseSignifyFetchJobModalParams): SignifyFetchJobModalProps & { openModal: () => void } => {
  const [open, setOpen] = useState(false);
  const { configDisabledReason } = useSignifyMappings();
  const activeJobId = useSnapshot(uiState.signifyFetchJob.activeJobIdByTable)[targetKey] ?? null;
  const { user } = useContext(UserContext);
  const userId = user?.uid;

  useEffect(() => {
    if (!enabled || activeJobId === null || userId === undefined) return undefined;

    const stores = createSignifyFetchJobStores(activeJobId, userId);
    void stores.job.connect();
    void stores.parts.connect();

    return () => {
      stores.job.disconnect();
      stores.parts.disconnect();
    };
  }, [activeJobId, enabled, userId]);

  useEffect(() => {
    if (!enabled || userId === undefined) return;
    if (uiState.signifyFetchJob.activeJobIdByTable[targetKey] !== undefined) return;

    void findReattachableSignifyFetchJob(userId, targetKey)
      .then((job) => {
        if (job !== null && uiState.signifyFetchJob.activeJobIdByTable[targetKey] === undefined) {
          uiState.signifyFetchJob.activeJobIdByTable[targetKey] = job.docId;
        }
      })
      .catch((error: unknown) => {
        console.warn('[useSignifyFetchJobModal] Could not reattach the fetch of', targetKey, error);
      });
  }, [enabled, targetKey, userId]);

  const openModal = useCallback(() => setOpen(true), []);
  const onCancel = useCallback(() => setOpen(false), []);

  return {
    openModal,
    open,
    onCancel,
    targetKey,
    hasQuantityColumn,
    disabled,
    configDisabledReason,
    onSuccess,
    onManualInsert,
    onFindInsertedRows,
  };
};
